import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useChallengeContext } from '../hooks/challenge/useChallengeContext'
import { useChallengeAccess } from '../hooks/membership/useChallengeAccess'
import { useLeaveTeam } from '../hooks/team/useLeaveTeam'
import { useTeamScreenData } from '../hooks/team/useTeamScreenData'
import { useTeamJoinFlow } from '../hooks/team/useTeamJoinFlow'
import { useTeam } from '../hooks/team/useTeam'
import { useUser } from '../hooks/user/useUser'
import { challengeTeamJoinPath, profilePath } from '../navigation'
import ActivityFeed from '../ui/home/ActivityFeed'
import AppShell from '../ui/layout/AppShell'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import Avatar, { getInitials } from '../ui/primitives/Avatar'
import BackButton from '../ui/primitives/BackButton'
import Button from '../ui/primitives/Button'
import Tabs from '../ui/primitives/Tabs'
import DifficultyCard from '../ui/team/DifficultyCard'
import RosterList from '../ui/team/RosterList'
import styles from '../ui/team/TeamScreen.module.css'
import TeamInviteModal from '../ui/team/TeamInviteModal'
import TeamLeaveModal from '../ui/team/TeamLeaveModal'
import TeamRenameModal from '../ui/team/TeamRenameModal'
import TrophyCase from '../ui/badges/TrophyCase'
import AppIcon from '../ui/icons/AppIcon'

type ModalState = 'none' | 'invite' | 'leave' | 'rename'
type TeamTab = 'members' | 'badges' | 'activity'

const TEAM_TABS = [
  { value: 'members', label: 'Members' },
  { value: 'badges', label: 'Badges' },
  { value: 'activity', label: 'Activity' },
] satisfies { value: TeamTab; label: string }[]

export default function TeamScreen() {
  const navigate = useNavigate()
  const { teamID } = useParams()
  const { data: user } = useUser()
  const { data: team, isLoading: teamLoading, error: teamError } = useTeam(teamID ?? '')
  const { data, isLoading: dataLoading } = useTeamScreenData(teamID ?? '')
  const { joinTeam, isJoiningTeam } = useTeamJoinFlow(teamID ?? '')
  const { mutate: leaveTeamMutation, isPending: isLeavingTeam } = useLeaveTeam()

  const { data: challengeContext } = useChallengeContext(team?.challengeID)
  const { teamID: viewerTeamID, isLoading: accessLoading } = useChallengeAccess(team?.challengeID)
  const [modal, setModal] = useState<ModalState>('none')
  const [tab, setTab] = useState<TeamTab>('members')

  const teamSizeMax = challengeContext?.teamSizeMax ?? 12

  if (!teamID) {
    return (
      <AppShell>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>Team not found</p>
      </AppShell>
    )
  }

  if (teamLoading || dataLoading || accessLoading) {
    return (
      <AppShell>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>Loading…</p>
      </AppShell>
    )
  }

  if (teamError || !team) {
    return (
      <AppShell>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>
          Could not load team.
        </p>
      </AppShell>
    )
  }

  const isInTeam = team.members.some((member) => member.id === user?.id)
  const isSpectator = !isInTeam && !!viewerTeamID && viewerTeamID !== team.id

  if (!isInTeam && !isSpectator) {
    return (
      <SimplePageLayout
        title={team.name}
        backTo={challengeTeamJoinPath(team.challengeID)}
      >
        <div className={styles.teamOf} style={{ marginBottom: 16 }}>
          Team of {team.members.length}
        </div>
        {team.isFull(teamSizeMax) ? (
          <div className={styles.joinCard}>
            <strong>This team is full</strong>
            <p>
              {team.members.length} / {teamSizeMax} members. Find another team or create your
              own.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.joinCard}>
              <strong>Join {team.name}?</strong>
              <p>Your progress will count toward this team&apos;s total.</p>
            </div>
            <Button onClick={joinTeam} disabled={isJoiningTeam}>
              {isJoiningTeam ? 'Joining…' : 'Join team'}
            </Button>
          </>
        )}
      </SimplePageLayout>
    )
  }

  if (!data) {
    return (
      <AppShell>
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px 0' }}>Loading…</p>
      </AppShell>
    )
  }

  const tabs = TEAM_TABS.filter((item) => item.value !== 'badges' || data.trophies.length > 0)
  const activeTab = tabs.some((item) => item.value === tab) ? tab : 'members'

  return (
    <>
      <AppShell>
        {isSpectator && (
          <div className={styles.back}>
            <BackButton onClick={() => navigate(-1)} />
          </div>
        )}
        <section className={styles.summaryCard}>
          <div className={styles.nameRow}>
            <h1 className={styles.name}>{team.name}</h1>
            {data.canRename && (
              <button
                type="button"
                className={styles.renameBtn}
                onClick={() => setModal('rename')}
                aria-label="Rename team"
              >
                <AppIcon icon="mdi:pencil-outline" size={18} />
              </button>
            )}
          </div>
          <div className={styles.summaryMeta}>
            <div className={styles.avatarStack} aria-label={`${team.members.length} team members`}>
              {team.members.slice(0, 6).map((member) => (
                <span key={member.id} className={styles.avatarItem}>
                  <Avatar
                    initials={getInitials(member.firstName, member.lastName)}
                    photoUrl={member.photoUrl}
                    size="sm"
                    variant={
                      member.id === user?.id
                        ? 'you'
                        : member.id === team.leaderID
                          ? 'leader'
                          : 'default'
                    }
                  />
                </span>
              ))}
              {team.members.length > 6 && (
                <span className={styles.avatarMore}>+{team.members.length - 6}</span>
              )}
            </div>
            <strong>{team.members.length} members</strong>
          </div>
        </section>

        <Tabs
          value={activeTab}
          onChange={setTab}
          items={tabs}
          variant="underline"
          listClassName={styles.tabs}
          ariaLabel="Team sections"
        />

        <div className={styles.tabPanel} role="tabpanel" aria-label={`${activeTab} tab`}>
          {activeTab === 'members' && (
            <>
              <RosterList
                members={data.roster}
                onSelect={(member) =>
                  navigate(`${profilePath(member.user.id)}?teamId=${team.id}`)
                }
              />

              {!isSpectator && (
                <DifficultyCard tiers={data.teamSizeTiers} activeTier={data.activeTier} />
              )}

              {data.canInvite && (
                <button
                  type="button"
                  className={styles.inviteBtn}
                  onClick={() => setModal('invite')}
                >
                  <AppIcon icon="mdi:plus" size={17} />
                  Invite member
                </button>
              )}

              {!isSpectator && (
                <div className={styles.leave}>
                  <button type="button" onClick={() => setModal('leave')} disabled={isLeavingTeam}>
                    Leave team
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === 'badges' && (
            <>
              <div className={styles.badgeCount}>
                {data.earnedCount} of {data.trophies.length} earned
              </div>
              <TrophyCase trophies={data.trophies} />
            </>
          )}

          {activeTab === 'activity' && <ActivityFeed items={data.feed} />}
        </div>
      </AppShell>

      {modal === 'invite' && (
        <TeamInviteModal team={team} onClose={() => setModal('none')} />
      )}

      {modal === 'rename' && (
        <TeamRenameModal team={team} onClose={() => setModal('none')} />
      )}

      {modal === 'leave' && (
        <TeamLeaveModal
          teamName={team.name}
          onCancel={() => setModal('none')}
          onConfirm={() => {
            leaveTeamMutation(team.id)
            setModal('none')
          }}
        />
      )}
    </>
  )
}
